import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { getClubEvents } from '../lib/firebase';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';
import PublishStateNote from '../components/PublishStateNote';
import DocumentsPasteImport from '../components/DocumentsPasteImport';
import {
  DOC_TYPE_LABELS,
  loadClubDocuments,
  loadSeason,
  loadBundle,
  loadRacetimeVariants,
  createDocument,
  updateDocument,
  deleteDocument,
  toggleDocSetLink,
  toggleSeasonDoc,
} from '../lib/documentStore';
import { uploadClubDocPdf } from '../lib/docUpload';
import { useAuth } from '../contexts/AuthContext';

function abbreviateEvent(series) {
  const m = series.match(
    /(Red|Blue|Green|Hut|Committee|West Pier|Corinthian|Freebird|Spring Chicken|Coastal).*?(Mon|Tue|Wed|Thu|Fri|Sat|Sun)/i,
  );
  if (m) return m[1].slice(0, 4) + ' ' + m[2];
  const s = series.replace(/^Course Card - /, '').replace(/ Fleet /i, ' ');
  return s.length > 15 ? s.slice(0, 15) : s;
}

function TypeBadge({ type }) {
  return (
    <span className={`doc-type-badge doc-type-${type}`}>
      {type.replace(/_/g, ' ')}
    </span>
  );
}

function TypeSelect({ value, onChange, id }) {
  return (
    <select id={id} className="inline-select" value={value} onChange={onChange}>
      {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
        <option key={k} value={k}>{v}</option>
      ))}
    </select>
  );
}

export default function DocumentManager() {
  const { clubId } = useParams();
  const { user } = useAuth();

  // Season selection
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState('');
  const [loadingSeasons, setLoadingSeasons] = useState(true);

  // Matrix data
  const [docMap, setDocMap] = useState({});
  const [season, setSeason] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [variants, setVariants] = useState({});
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [error, setError] = useState(null);

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [changeLog, setChangeLog] = useState([]);

  // Add form
  const [addId, setAddId] = useState('');
  const [addTitle, setAddTitle] = useState('');
  const [addType, setAddType] = useState('other_doc');
  // New documents default to Public — see createDocument in documentStore.
  const [addFree, setAddFree] = useState(true);
  const [addUrl, setAddUrl] = useState('');

  // Paste import
  const [showPasteImport, setShowPasteImport] = useState(false);

  // Edit form
  const [editTitle, setEditTitle] = useState('');
  const [editType, setEditType] = useState('');
  const [editFree, setEditFree] = useState(false);
  const [editUrl, setEditUrl] = useState('');

  // PDF upload (ra-8ggy) — uploads to /cgc/{clubId}/{uploaderHash}/{docId}.pdf
  // and drops the resulting download URL into the URL field.
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  async function handleUploadPdf(docId, file, setUrl) {
    if (!file) return;
    const id = (docId || '').trim();
    if (!id) {
      setUploadError('Enter a doc-id first, then upload the PDF.');
      return;
    }
    setUploadError(null);
    setUploadingDocId(id);
    try {
      const url = await uploadClubDocPdf({
        clubId,
        uid: user?.uid,
        docId: id,
        file,
      });
      setUrl(url);
      logChange(`Uploaded PDF for ${id} (${(file.size / 1024 / 1024).toFixed(1)} MB)`);
    } catch (err) {
      setUploadError(err?.message || 'Upload failed.');
    } finally {
      setUploadingDocId(null);
    }
  }

  function logChange(msg) {
    setChangeLog((prev) => [...prev, { time: new Date().toLocaleTimeString(), msg }]);
  }

  // Load seasons for this club
  useEffect(() => {
    if (!clubId) return;
    setLoadingSeasons(true);
    getClubEvents(clubId)
      .then((evts) => {
        const sorted = [...evts].sort((a, b) => (b.id > a.id ? 1 : -1));
        setSeasons(sorted);
        if (sorted.length > 0) setSeasonId(sorted[0].id);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingSeasons(false));
  }, [clubId]);

  // Load matrix data
  const loadMatrix = useCallback(async () => {
    if (!clubId || !seasonId) return;
    setLoadingMatrix(true);
    setError(null);
    try {
      const [seasonData, docs, vars] = await Promise.all([
        loadSeason(clubId, seasonId),
        loadClubDocuments(clubId),
        loadRacetimeVariants(clubId, seasonId),
      ]);
      if (!seasonData) {
        setError('Season not found.');
        return;
      }
      const bun = await loadBundle(clubId, seasonId, seasonData.content_version || 1);
      setSeason(seasonData);
      setDocMap(docs);
      setBundle(bun);
      setVariants(vars);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMatrix(false);
    }
  }, [clubId, seasonId]);

  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  // Derived data
  const matrixData = useMemo(() => {
    if (!season) return null;

    const seasonDocIds = season.documents || [];
    const bundleDocs = (bundle && bundle.documents) || [];
    const bundleDocMap = {};
    bundleDocs.forEach((bd) => { bundleDocMap[bd.id] = bd; });
    const courseCards = (bundle && bundle.course_cards) || [];

    // Variant document_set_ids
    const variantDocSetIds = {};
    Object.values(variants).forEach((v) => {
      if (v.document_set_id) variantDocSetIds[v.document_set_id] = v.name || v.id;
    });

    // Build season docs
    const seasonDocs = seasonDocIds.map((id) => {
      const fsDoc = docMap[id];
      const bDoc = bundleDocMap[id];
      return {
        id,
        title: fsDoc?.title || bDoc?.title || id,
        type: fsDoc?.type || bDoc?.type || 'other',
        // Nullish, not `||` (ra-hdil.1): an explicit `is_free:false` in
        // Firestore must beat the bundle's stale value, and a doc that carries
        // the flag on neither side is free — the app reads it the same way.
        is_free: fsDoc?.is_free ?? bDoc?.isFree ?? true,
        url: fsDoc?.url || bDoc?.url || null,
        document_set_ids: fsDoc?.document_set_ids || bDoc?.document_set_ids || [],
      };
    });

    // Collect all document_set_ids
    const allDocSetIds = {};
    seasonDocs.forEach((d) => (d.document_set_ids || []).forEach((dsid) => { allDocSetIds[dsid] = true; }));
    Object.keys(variantDocSetIds).forEach((dsid) => { allDocSetIds[dsid] = true; });

    // Build document set objects
    const docSets = {};
    bundleDocs.forEach((bd) => {
      (bd.document_set_ids || []).forEach((dsid) => {
        if (!docSets[dsid]) docSets[dsid] = { id: dsid, series: '', documentIds: [], courseSetId: null };
        if (!docSets[dsid].documentIds.includes(bd.id)) docSets[dsid].documentIds.push(bd.id);
      });
    });
    Object.keys(variantDocSetIds).forEach((dsid) => {
      if (!docSets[dsid]) docSets[dsid] = { id: dsid, series: variantDocSetIds[dsid], documentIds: [], courseSetId: null };
    });

    // Derive series names
    Object.values(docSets).forEach((ds) => {
      if (!ds.series) {
        for (const did of ds.documentIds) {
          const d = docMap[did];
          if (d?.type === 'course_card') { ds.series = d.title || ''; break; }
        }
      }
      if (!ds.series) {
        for (const did of ds.documentIds) {
          const d = docMap[did];
          if (d?.series) { ds.series = d.series; break; }
        }
      }
      if (!ds.series) ds.series = ds.id;
    });

    // Match course_set_ids
    Object.values(docSets).forEach((ds) => {
      for (const did of ds.documentIds) {
        const d = docMap[did];
        if (d?.type === 'course_card') {
          for (const cc of courseCards) {
            const ccName = (cc.name || '').toLowerCase();
            const dTitle = (d.title || '').toLowerCase();
            if (ccName && dTitle && ccName.includes(dTitle.split(' - ').pop().trim().split(' ')[0].toLowerCase())) {
              ds.courseSetId = cc.id;
            }
          }
        }
      }
    });

    const relevantSets = Object.keys(allDocSetIds)
      .map((id) => docSets[id])
      .filter(Boolean)
      .sort((a, b) => a.series.localeCompare(b.series));

    // Race dates class map
    const courseClassMap = {};
    const raceDates = (bundle && bundle.race_dates) || [];
    raceDates.forEach((rd) => {
      const key = rd.course || '';
      if (!courseClassMap[key]) courseClassMap[key] = {};
      Object.keys(rd.classes || {}).forEach((c) => { courseClassMap[key][c] = true; });
    });
    const classNameMap = {};
    (season.classes || []).forEach((c) => { classNameMap[c.id] = c.name; });

    // Gap detection
    const seasonDocSet = new Set(seasonDocIds);
    const orphanDocs = Object.values(docMap).filter((d) => !seasonDocSet.has(d.id));
    let missingSetLinkCount = 0;
    seasonDocs.forEach((d) => {
      if (!(d.document_set_ids || []).some((dsid) => !!docSets[dsid])) missingSetLinkCount++;
    });
    const setsWithNoCourseCard = relevantSets.filter(
      (ds) => !ds.documentIds.some((did) => docMap[did]?.type === 'course_card'),
    );

    return {
      seasonDocs,
      relevantSets,
      docSets,
      courseClassMap,
      classNameMap,
      orphanDocs,
      missingSetLinkCount,
      setsWithNoCourseCard,
    };
  }, [season, bundle, variants, docMap]);

  // Handlers
  async function handleToggleDocSet(docId, docSetId, currentlyLinked) {
    try {
      await toggleDocSetLink(clubId, seasonId, docId, docSetId, currentlyLinked);
      logChange(`${currentlyLinked ? 'Unlinked' : 'Linked'} ${docId} \u2194 ${docSetId}`);
      await loadMatrix();
    } catch (err) { setError(err.message); }
  }

  async function handleToggleSeasonDoc(docId, inSeason) {
    try {
      await toggleSeasonDoc(clubId, seasonId, docId, inSeason);
      logChange(`${inSeason ? 'Removed from' : 'Added to'} season: ${docId}`);
      await loadMatrix();
    } catch (err) { setError(err.message); }
  }

  function startEdit(d) {
    if (editingDocId === d.id) { setEditingDocId(null); return; }
    setEditingDocId(d.id);
    setEditTitle(d.title);
    setEditType(d.type);
    setEditFree(d.is_free);
    setEditUrl(d.url || '');
  }

  async function handleSaveEdit(docId) {
    if (!editTitle.trim()) return;
    try {
      await updateDocument(clubId, docId, {
        title: editTitle.trim(),
        type: editType,
        isFree: editFree,
        url: editUrl.trim(),
      });
      logChange(`Updated ${docId}: ${editTitle.trim()}`);
      setEditingDocId(null);
      await loadMatrix();
    } catch (err) { setError(err.message); }
  }

  async function handleDelete(docId, title) {
    if (!window.confirm(`Delete "${title}"?\n\nThis removes the document and unlinks it from this season.`)) return;
    try {
      await deleteDocument(clubId, seasonId, docId);
      logChange(`Deleted ${docId}: ${title}`);
      await loadMatrix();
    } catch (err) { setError(err.message); }
  }

  async function handleAdd() {
    if (!addId.trim() || !addTitle.trim()) return;
    try {
      await createDocument(clubId, seasonId, {
        id: addId.trim(),
        title: addTitle.trim(),
        type: addType,
        isFree: addFree,
        url: addUrl.trim(),
      });
      logChange(`Created ${addId.trim()}: ${addTitle.trim()}`);
      setAddId('');
      setAddTitle('');
      setAddUrl('');
      setAddFree(true);
      await loadMatrix();
    } catch (err) { setError(err.message); }
  }

  async function handleBulkImport(docs) {
    try {
      for (const d of docs) {
        await createDocument(clubId, seasonId, d);
        logChange(`Created ${d.id}: ${d.title}`);
      }
      setShowPasteImport(false);
      await loadMatrix();
    } catch (err) { setError(err.message); }
  }

  function handleToggleEditMode() {
    setEditMode(!editMode);
    setEditingDocId(null);
    if (!editMode) setChangeLog([]);
  }

  return (
    <>
      <PortalHeader />
      <div className="page" style={{ maxWidth: 1400 }}>
        <PageHeader backTo={`/clubs/${clubId}`} backLabel={clubId} title="Documents" />

      <PublishStateNote mode="instant">
        <p>
          Documents, links and season assignments save immediately — no publish
          step. Users see them the next time the app loads this club.
        </p>
      </PublishStateNote>

      {error && <div className="error-banner">{error}</div>}

      {/* Season selector + Edit toggle */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          Season:
          <select className="inline-select" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} disabled={loadingSeasons}>
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.id} ({(s.documents || []).length} docs)
              </option>
            ))}
          </select>
        </label>
        <button
          className={`btn btn-sm${editMode ? ' btn-primary' : ''}`}
          onClick={handleToggleEditMode}
        >
          {editMode ? 'Done' : 'Edit'}
        </button>
        {editMode && (
          <button
            className={`btn btn-sm${showPasteImport ? ' btn-primary' : ''}`}
            onClick={() => setShowPasteImport(!showPasteImport)}
          >
            Paste Import
          </button>
        )}
        {changeLog.length > 0 && (
          <div style={{ flex: 1, fontSize: '0.75rem', background: '#f5f5f5', padding: '0.4rem 0.6rem', borderRadius: 4, maxHeight: 80, overflowY: 'auto' }}>
            <strong style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#888' }}>Changes:</strong>
            <ul style={{ margin: '0.2rem 0 0', paddingLeft: '1.2rem' }}>
              {changeLog.map((c, i) => (
                <li key={i}><span style={{ color: '#888', fontSize: '0.7rem' }}>{c.time}</span> {c.msg}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {loadingMatrix && <p>Loading from Firestore...</p>}

      {!loadingMatrix && matrixData && (
        <>
          {/* Stats */}
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem', padding: '0.6rem 1rem', background: '#fafafa', borderRadius: 6, fontSize: '0.85rem' }}>
            <span><strong>{matrixData.seasonDocs.length}</strong> documents in season</span>
            <span><strong>{matrixData.missingSetLinkCount}</strong> unlinked to document sets</span>
            <span><strong>{matrixData.relevantSets.length}</strong> document sets</span>
            <span><strong>{matrixData.setsWithNoCourseCard.length}</strong> sets without course card</span>
            {matrixData.orphanDocs.length > 0 && (
              <span style={{ color: '#e65100' }}><strong>{matrixData.orphanDocs.length}</strong> club docs not in this season</span>
            )}
          </div>

          {/* Matrix table */}
          <div style={{ overflowX: 'auto' }}>
            <table className="config-table" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  {editMode && <th>In Season</th>}
                  <th style={{ textAlign: 'left', minWidth: 200 }}>Document</th>
                  <th>Type</th>
                  <th>Public</th>
                  {matrixData.relevantSets.map((ds) => (
                    <th key={ds.id} title={`${ds.series} [${ds.id}]`} style={{ textAlign: 'center', fontSize: '0.7rem' }}>
                      {abbreviateEvent(ds.series)}
                      <br /><code style={{ fontSize: '0.55rem', color: '#888' }}>{ds.id}</code>
                    </th>
                  ))}
                  <th>Classes</th>
                  {editMode && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {matrixData.seasonDocs.map((d) => {
                  const hasSetLink = (d.document_set_ids || []).some(
                    (dsid) => !!matrixData.docSets[dsid],
                  );
                  const membership = new Set(d.document_set_ids || []);

                  // Classes for course_card docs
                  let classText = 'All';
                  if (d.type === 'course_card') {
                    let courseSetId = null;
                    for (const dsid of d.document_set_ids || []) {
                      const ds = matrixData.docSets[dsid];
                      if (ds?.courseSetId) { courseSetId = ds.courseSetId; break; }
                    }
                    if (courseSetId && matrixData.courseClassMap[courseSetId]) {
                      const names = Object.keys(matrixData.courseClassMap[courseSetId])
                        .map((cid) => matrixData.classNameMap[cid] || cid);
                      if (names.length) classText = names.join(', ');
                    }
                  }

                  return (
                    <Fragment key={d.id}>
                      <tr style={!hasSetLink ? { background: '#fff8e1' } : undefined}>
                        {editMode && (
                          <td style={{ textAlign: 'center' }}>
                            <input type="checkbox" checked onChange={() => handleToggleSeasonDoc(d.id, true)} />
                          </td>
                        )}
                        <td style={{ textAlign: 'left' }}>
                          {d.url ? (
                            <a href={d.url} target="_blank" rel="noreferrer" style={{ color: '#1a1a2e' }}>{d.title}</a>
                          ) : d.title}
                          {editMode && <><br /><code style={{ fontSize: '0.6rem', color: '#888' }}>{d.id}</code></>}
                        </td>
                        <td style={{ textAlign: 'center' }}><TypeBadge type={d.type} /></td>
                        <td style={{ textAlign: 'center' }}>
                          {/* The app now acts on this flag (ra-hdil.1), so the
                              paid state gets a label. The old em-dash read as
                              "not set", not "hidden from users without the
                              season's content". */}
                          {d.is_free
                            ? <span style={{ color: '#2e7d32', fontWeight: 700 }}>Public</span>
                            : <span style={{ color: '#b26500', fontWeight: 700 }} title="Hidden in the app for users without this season's content">Paid</span>}
                        </td>
                        {matrixData.relevantSets.map((ds) => {
                          const linked = membership.has(ds.id);
                          if (editMode) {
                            return (
                              <td
                                key={ds.id}
                                style={{ textAlign: 'center', cursor: 'pointer' }}
                                onClick={() => handleToggleDocSet(d.id, ds.id, linked)}
                              >
                                {linked
                                  ? <span style={{ color: '#2e7d32', fontWeight: 700 }}>{'\u2713'}</span>
                                  : <span style={{ color: '#ddd' }}>{'\u2014'}</span>}
                              </td>
                            );
                          }
                          return (
                            <td key={ds.id} style={{ textAlign: 'center' }}>
                              {linked
                                ? <span style={{ color: '#2e7d32', fontWeight: 700 }}>{'\u2713'}</span>
                                : <span style={{ color: '#ddd' }}>{'\u2014'}</span>}
                            </td>
                          );
                        })}
                        <td style={{ fontSize: '0.7rem', textAlign: 'left', color: classText === 'All' ? '#888' : undefined }}>
                          {classText}
                        </td>
                        {editMode && (
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <button className="btn btn-sm" onClick={() => startEdit(d)} title="Edit">{'\u270E'}</button>
                            {' '}
                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(d.id, d.title)} title="Delete">{'\u{1F5D1}'}</button>
                          </td>
                        )}
                      </tr>
                      {editMode && editingDocId === d.id && (
                        <tr style={{ background: '#f5f5f5' }}>
                          <td colSpan={4 + matrixData.relevantSets.length + 2 + (editMode ? 1 : 0)} style={{ padding: '0.5rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                              <label className="form-label">Title:</label>
                              <input className="inline-input" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} style={{ flex: 1, minWidth: 150 }} />
                              <label className="form-label">Type:</label>
                              <TypeSelect value={editType} onChange={(e) => setEditType(e.target.value)} />
                              <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <input type="checkbox" checked={editFree} onChange={(e) => setEditFree(e.target.checked)} /> Public
                              </label>
                              <label className="form-label">URL:</label>
                              <input className="inline-input" value={editUrl} onChange={(e) => setEditUrl(e.target.value)} placeholder="paste a public link, or upload a PDF →" style={{ flex: 2, minWidth: 200 }} />
                              <label className="btn btn-sm" title="Upload a PDF instead of pasting a link" style={{ margin: 0, cursor: uploadingDocId === d.id ? 'wait' : 'pointer' }}>
                                {uploadingDocId === d.id ? 'Uploading…' : 'Upload PDF'}
                                <input
                                  type="file"
                                  accept="application/pdf"
                                  disabled={uploadingDocId === d.id}
                                  style={{ display: 'none' }}
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = ''; // allow re-selecting the same file
                                    handleUploadPdf(d.id, f, setEditUrl);
                                  }}
                                />
                              </label>
                              <button className="btn btn-sm btn-primary" onClick={() => handleSaveEdit(d.id)}>Save</button>
                              <button className="btn btn-sm" onClick={() => setEditingDocId(null)}>Cancel</button>
                              {uploadError && uploadingDocId === null && (
                                <span style={{ color: '#c00', fontSize: '0.75rem', width: '100%' }}>{uploadError}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Orphan docs */}
          {matrixData.orphanDocs.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ fontSize: '0.85rem', color: '#e65100', marginBottom: '0.5rem' }}>
                Unlinked Club Documents (not in this season)
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {matrixData.orphanDocs.map((d) => (
                  <span
                    key={d.id}
                    className={`doc-type-badge doc-type-${d.type || 'other'}`}
                    style={{
                      fontSize: '0.7rem',
                      padding: '0.2rem 0.5rem',
                      cursor: editMode ? 'pointer' : undefined,
                    }}
                    onClick={editMode ? () => handleToggleSeasonDoc(d.id, false) : undefined}
                    title={editMode ? 'Click to add to season' : undefined}
                  >
                    {editMode ? '+ ' : ''}{d.title || d.id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Paste import */}
          {editMode && showPasteImport && (
            <div style={{ marginTop: '1rem' }}>
              <DocumentsPasteImport
                existingDocIds={Object.keys(docMap)}
                onImport={handleBulkImport}
                onClose={() => setShowPasteImport(false)}
              />
            </div>
          )}

          {/* Add document form */}
          {editMode && (
            <div style={{
              marginTop: '1rem', padding: '0.75rem 1rem', background: '#f5f5f5',
              borderRadius: 6, display: 'flex', gap: '0.5rem', alignItems: 'center',
              flexWrap: 'wrap', fontSize: '0.8rem',
            }}>
              <strong style={{ fontSize: '0.8rem' }}>Add Document:</strong>
              <input className="inline-input" placeholder="doc-id" value={addId} onChange={(e) => setAddId(e.target.value)} style={{ width: 120 }} />
              <input className="inline-input" placeholder="Title" value={addTitle} onChange={(e) => setAddTitle(e.target.value)} style={{ flex: 1, minWidth: 150 }} />
              <TypeSelect value={addType} onChange={(e) => setAddType(e.target.value)} />
              <label style={{ fontSize: '0.75rem' }}>
                <input type="checkbox" checked={addFree} onChange={(e) => setAddFree(e.target.checked)} /> Public
              </label>
              <input className="inline-input" placeholder="paste a public link, or upload a PDF →" value={addUrl} onChange={(e) => setAddUrl(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
              <label className="btn btn-sm" title="Upload a PDF instead of pasting a link (enter a doc-id first)" style={{ margin: 0, cursor: uploadingDocId === addId.trim() && addId.trim() ? 'wait' : 'pointer' }}>
                {uploadingDocId && uploadingDocId === addId.trim() ? 'Uploading…' : 'Upload PDF'}
                <input
                  type="file"
                  accept="application/pdf"
                  disabled={!!uploadingDocId}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    handleUploadPdf(addId, f, setAddUrl);
                  }}
                />
              </label>
              <button className="btn btn-sm btn-primary" onClick={handleAdd}>+ Add</button>
            </div>
          )}
          {editMode && uploadError && (
            <div style={{ color: '#c00', fontSize: '0.75rem', marginTop: '0.25rem' }}>{uploadError}</div>
          )}
        </>
      )}
      </div>
    </>
  );
}
